import { Controller } from "@hotwired/stimulus"

export default class extends Controller {

    static targets = [ "form", "submitButton", "submitSpinner" ]
    static values = {
        keepOpenOnSuccess: { type: Boolean, default: false },
        cancelUrl: String,
        cancelMethod: { type: String, default: "patch" }
    }

    formTargetConnected(target) {
        target.addEventListener('turbo:submit-end', (event) => {
            if (event.detail.success) {
                this.submittedSuccessfully = true
                if (!this.keepOpenOnSuccessValue) this.hide()
            } else {
                if (this.hasSubmitButtonTarget) {
                    this.submitButtonTarget.disabled = false
                }
                if (this.hasSubmitSpinnerTarget) {
                    this.submitSpinnerTarget.classList.remove("d-inline-block")
                    this.submitSpinnerTarget.classList.add("d-none")
                }
            }
        })
    }

    connect() {
        this.dialog = new bootstrap.Dialog(this.element)
        this.cancelRequested = false
        this.cancelPerformed = false
        this.submittedSuccessfully = false
        this.dialog.show()

        this.element.addEventListener("form:state", (event) => {
            this.toggleSubmit(event.detail.valid)
        })
    }

    toggleSubmit(isValid) {
        if (!this.hasSubmitButtonTarget) return

        this.submitButtonTarget.disabled = !isValid
    }

    submit() {
        if (this.hasFormTarget) {
            if (this.formTarget.reportValidity()) {
                this.cancelRequested = false
                this.formTarget.requestSubmit()
                if (this.hasSubmitButtonTarget) {
                    this.submitButtonTarget.disabled = true
                }
                if (this.hasSubmitSpinnerTarget) {
                    this.submitSpinnerTarget.classList.remove("d-none")
                    this.submitSpinnerTarget.classList.add("d-inline-block")
                }
            }
        }
    }

    refresh(event) {
        const form = event.target.form
        if (!form || !form.reportValidity()) return

        // Finish hiding before replacing the frame so Bootstrap removes this
        // dialog's backdrop and restores scrolling before the next dialog opens.
        if (this.isOpen()) {
            this.element.addEventListener("hidden.bs.dialog", () => form.requestSubmit(), { once: true })
            this.hide()
        } else {
            form.requestSubmit()
        }
    }

    cancel() {
        if (!this.hasCancelUrlValue || this.cancelPerformed) return

        this.cancelRequested = true
    }

    hideBeforeRender(event) {
        if (this.isOpen()) {
            event.preventDefault()
            this.element.addEventListener('hidden.bs.dialog', event.detail.resume)
            this.dialog.hide()
        }
    }

    async handleHidden() {
        if (!this.cancelRequested || this.submittedSuccessfully || !this.hasCancelUrlValue || this.cancelPerformed) return

        this.cancelPerformed = true

        const response = await fetch(this.cancelUrlValue, {
            method: this.cancelMethodValue.toUpperCase(),
            headers: {
                "Accept": "text/vnd.turbo-stream.html",
                "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content
            },
            credentials: "same-origin"
        })

        const body = await response.text()
        if (body.length > 0 && window.Turbo) {
            window.Turbo.renderStreamMessage(body)
        }
    }

    isOpen() {
        return this.element.open
    }

    hide() {
        if (this.isOpen()) {
            this.dialog.hide()
            // Bootstrap ignores hide() while the opening animation is running.
            // A quick successful submission must still close once it finishes.
            if (this.isOpen()) {
                this.element.addEventListener("shown.bs.dialog", () => this.dialog.hide(), { once: true })
            }
        }
    }

    disconnect() {
        this.dialog?.dispose()
        this.element.remove()
    }
}
